<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, inject, watch, onBeforeMount } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useTaskStore } from "@core/stores/task.store"
import { useFetchWrapper } from "@core/composables"
import { formatDate } from "@core/helpers"
import type { Services } from "@services"
import moment from "moment-timezone"
import type { EnergyManagerIotAnalyse } from "@types"
import { WEATHER_STATION_TYPES_LIST } from "@core/constants"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Analyse température ambiance",
            create_analyse: "Lancer une nouvelle analyse",
            last_update: "Dernière mise à jour :",
            start_synthesis: "Lancer une synthèse",
            no_synthesis:
                "Aucune synthèse disponible. Lancez une première analyse pour commencer.",
            download_report: "Télécharger le rapport",
            error: "Pour afficher les analyses, il faut au minimum un capteur de température intérieure et un capteur de température extérieure sur le bâtiment.",
            add_sensor: "Ajouter un capteur",
            task_label: "Analyse température ambiance en cours...",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

// ================================
// COMPOSABLES & SERVICES
// ================================
// Gestion défensive du router pour Storybook
// TODO: A retirer quand on aura installé le package vue-router-storybook
const route = useRoute()
const buildingStore = useBuildingStore()
const taskStore = useTaskStore()
const { $getBuildingIotAnalyses, $generateIotAnalyseReport } =
    inject<Services>("services")!

// ================================
// ÉTAT DES DONNÉES
// ================================
/**
 * Liste des analyses IoT du bâtiment
 */
const analyses = ref<EnergyManagerIotAnalyse[]>([])

/**
 * Erreur lors de la récupération des analyses
 */
const errorFetch = ref<string | null>(null)

/**
 * État de la modal de création d'analyse
 */
const isDialogVisible = ref(false)

/**
 * État de la modal d'ajout de capteur
 */
const isAddSensorDialogVisible = ref(false)

// ================================
// RÉCUPÉRATION DES DONNÉES
// ================================
/**
 * Récupère les analyses IoT du bâtiment
 */
const {
    fetch: fetchAnalyses,
    isFetching: isFetchingAnalyses,
    hasError: hasErrorAnalyses,
    error: errorAnalyses,
} = useFetchWrapper(
    async (buildingId: string, controller?: AbortController) => {
        if (!buildingId) return

        try {
            const data = await $getBuildingIotAnalyses(buildingId, controller)
            analyses.value = data
            errorFetch.value = null
        } catch (err: unknown) {
            errorFetch.value =
                err instanceof Error
                    ? err.message
                    : "Erreur lors de la récupération des analyses"
            throw err
        }
    }
)

// ================================
// COMPUTED
// ================================

/**
 * Récupère la dernière analyse (tous statuts confondus), triée par updatedAt
 */
const getLastAnalyseAllStatus = computed<EnergyManagerIotAnalyse | null>(() => {
    const sorted = [...analyses.value].sort((a, b) => {
        if (a.updatedAt < b.updatedAt) return 1
        if (a.updatedAt > b.updatedAt) return -1
        return 0
    })
    return sorted[0] || null
})

/**
 * Récupère la dernière analyse en SUCCESS, triée par updatedAt
 */
const getLastAnalyse = computed<EnergyManagerIotAnalyse | null>(() => {
    const successAnalyses = analyses.value.filter((a) => a.status === "SUCCESS")
    const sorted = [...successAnalyses].sort((a, b) => {
        if (a.updatedAt < b.updatedAt) return 1
        if (a.updatedAt > b.updatedAt) return -1
        return 0
    })
    return sorted[0] || null
})

/**
 * Date de la dernière mise à jour formatée
 */
const getLastUpdate = computed(() => {
    if (!getLastAnalyse.value?.updatedAt) return ""
    return formatDate(getLastAnalyse.value.updatedAt)
})

/**
 * Message d'erreur à afficher
 */
const getErrorMessage = computed(() => {
    return (
        errorFetch.value ||
        (hasErrorAnalyses.value ? errorAnalyses.value?.message : null)
    )
})

/**
 * Vérifie si le bâtiment a au moins un capteur de température intérieure et un capteur de température extérieure
 */
const getHasTempSensor = computed(() => {
    const sources: Source[] = buildingStore.building?.sources ?? []

    if (!sources.length) return false

    // Vérifie s'il y a au moins un capteur IoT avec température intérieure
    const hasInteriorTempSensor = sources.some(
        (source) => !!source.monitoring?.lastdate?.temperature_air_interieur
    )

    // Vérifie s'il y a au moins une source météo (température extérieure)
    const hasExteriorTempSensor = sources.some((source) =>
        WEATHER_STATION_TYPES_LIST.includes(source.idtype)
    )

    return hasInteriorTempSensor && hasExteriorTempSensor
})

/**
 * État global de chargement (analyses, sources ou building)
 */
const getIsLoading = computed(() => {
    return (
        isFetchingAnalyses.value ||
        buildingStore.isFetchingBuildingSources ||
        buildingStore.isFetchingBuilding
    )
})

// ================================
// ACTIONS UTILISATEUR
// ================================
/**
 * Rafraîchit la liste des analyses du bâtiment courant (utilisé par CreateAnalyseIotDialog).
 */
async function refetchAnalysesForCurrentBuilding() {
    const id = buildingStore.building?._id
    if (id) await fetchAnalyses(id)
}

/**
 * Ouvre la modal de création d'analyse
 */
function onClickCreateAnalyse() {
    isDialogVisible.value = true
}

/**
 * Ferme la modal de création d'analyse
 */
function onCancelDialog() {
    isDialogVisible.value = false
}

function onOpenReportFromLastAnalyse() {
    // Si le rapport existe et n'est pas expiré, télécharger directement
    if (
        getLastAnalyse.value?.url_rapport &&
        getLastAnalyse.value?.date_expiration_rapport &&
        moment(getLastAnalyse.value?.date_expiration_rapport).isAfter(moment())
    ) {
        window.location.assign(getLastAnalyse.value?.url_rapport || "")
        return true
    }
}

/**
 * Télécharge le rapport de l'analyse
 */
async function onClickDownloadReport() {
    if (!getLastAnalyse.value) return

    const hasOpen = onOpenReportFromLastAnalyse()

    if (hasOpen) return

    // Sinon, lancer la génération du rapport
    try {
        const { task_id, event_name } = await $generateIotAnalyseReport(
            getLastAnalyse.value._id
        )

        taskStore.followEvent(task_id, event_name, {
            taskname: "analyse.iot.rapport",
            taskToastTitle: "Génération du rapport en cours...",
            notification: true,
            autoDelete: true,
            onSuccess: (data: unknown) => {
                // Si on reçoit l'URL dans la réponse, télécharger
                const response = data as {
                    data?: { url_rapport?: string }
                }
                if (response?.data?.url_rapport) {
                    window.location.assign(response.data?.url_rapport || "")
                } else {
                    // Sinon, rafraîchir les analyses pour récupérer l'URL
                    if (buildingStore.building?._id) {
                        fetchAnalyses(buildingStore.building._id).then(() => {
                            onOpenReportFromLastAnalyse()
                        })
                    }
                }
            },
        })
    } catch (err: unknown) {
        console.error("Erreur lors de la génération du rapport:", err)
    }
}

/**
 * Ouvre la modal d'ajout de capteur
 */
function onAddSensor() {
    isAddSensorDialogVisible.value = true
}

/**
 * Gère l'annulation de l'ajout de capteur
 */
function onCancelAddSensor() {
    isAddSensorDialogVisible.value = false
}

/**
 * Gère la fin de l'ajout de capteur
 */
async function onDoneAddSensor() {
    isAddSensorDialogVisible.value = false
    // Recharge le bâtiment et ses sources pour mettre à jour la liste
    const buildingId = route?.params?._id_building as string | undefined
    if (buildingId && !Array.isArray(buildingId)) {
        // Lancer en parallèle
        await Promise.all([
            // Recharge les analyses du bâtiment
            fetchAnalyses(buildingId),
        ])
    }
}

// ================================
// LIFECYCLE
// ================================
/**
 * Initialise les données au montage
 */
onBeforeMount(async () => {
    const buildingId = route?.params?._id_building as string | undefined
    if (buildingId && !Array.isArray(buildingId)) {
        await fetchAnalyses(buildingId)
    }
})

/**
 * Surveille les changements de route pour recharger les données
 */
watch(
    () => route?.params?._id_building as string | undefined,
    async (buildingId) => {
        if (buildingId && !Array.isArray(buildingId)) {
            await fetchAnalyses(buildingId)
        }
    }
)
</script>

<template>
    <div class="page page-building-analyse-temp-ambiance">
        <!-- ================================ -->
        <!-- HEADER -->
        <!-- ================================ -->
        <HeaderPageBuilding
            :title="t('title')"
            class-header="flex flex-row flex-wrap gap-4 items-center justify-between"
        >
            <BaseButton
                :label="t('create_analyse')"
                :disabled="getIsLoading || !getHasTempSensor"
                @click="onClickCreateAnalyse"
                class="flex-1 max-w-fit"
            />
        </HeaderPageBuilding>

        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL -->
        <!-- ================================ -->
        <BaseSkeleton v-if="getIsLoading" width="100%" height="20rem" />
        <div v-else>
            <!-- Si on a au moins un capteur de température sur le bâtiment -->
            <div
                v-if="getHasTempSensor"
                class="flex flex-col items-center gap-8 pb-8"
            >
                <!-- Affiche les informations sur la dernière mise à jour ou un message -->
                <p
                    v-if="getLastAnalyse"
                    class="flex items-center flex-wrap gap-4 text-sm"
                >
                    <span>{{ t("last_update") }}</span>
                    <span class="font-semibold">{{ getLastUpdate }}</span>
                    <BaseButton
                        text
                        size="small"
                        icon="pi pi-refresh"
                        :label="t('start_synthesis')"
                        @click="onClickCreateAnalyse"
                    />
                </p>
                <p
                    v-else-if="!getLastAnalyse && !getErrorMessage"
                    class="text-sm"
                >
                    {{ t("no_synthesis") }}
                </p>

                <!-- Information sur les paramètres utilisées -->
                <AnalyseTempAmbianceLastParams
                    v-if="getLastAnalyseAllStatus?.inputs"
                    :date-from="getLastAnalyseAllStatus.inputs.date_from"
                    :date-to="getLastAnalyseAllStatus.inputs.date_to"
                    :temperature-utilisation="
                        getLastAnalyseAllStatus.inputs.temperature_utilisation
                    "
                    :temperature-nonutilisation="
                        getLastAnalyseAllStatus.inputs
                            .temperature_nonutilisation
                    "
                />

                <!-- Bouton pour lancer l'analyse -->
                <BaseButton
                    v-if="!getLastAnalyse && !getErrorMessage"
                    :label="t('start_synthesis')"
                    severity="secondary"
                    size="small"
                    icon="pi pi-refresh"
                    @click="onClickCreateAnalyse"
                />

                <!-- Message d'erreur en cas d'aucune analyse en succès -->
                <BaseMessage
                    v-if="getErrorMessage"
                    severity="error"
                    class="w-full"
                >
                    {{ getErrorMessage }}
                </BaseMessage>

                <!-- Bouton télécharger le rapport -->
                <BaseButton
                    v-if="getLastAnalyse"
                    :label="t('download_report')"
                    size="small"
                    @click="onClickDownloadReport"
                />
            </div>
            <!-- Sinon affiche un message d'erreur et un bouton pour ajouter un capteur -->
            <div
                v-else
                class="flex flex-col gap-8 items-center text-center justify-center mt-8"
            >
                <h1 class="text-sm">
                    {{ t("error") }}
                </h1>
                <BaseButton
                    :label="t('add_sensor')"
                    size="small"
                    @click="onAddSensor"
                />
            </div>
        </div>

        <!-- ================================ -->
        <!-- MODAL DE CRÉATION D'ANALYSE -->
        <!-- ================================ -->
        <CreateAnalyseIotDialog
            v-model:visible="isDialogVisible"
            :building-id="buildingStore.building?._id"
            :initial-values="getLastAnalyse?.inputs ?? null"
            @refetch="refetchAnalysesForCurrentBuilding"
            @cancel="onCancelDialog"
        />

        <!-- ================================ -->
        <!-- MODAL D'AJOUT DE CAPTEUR -->
        <!-- ================================ -->
        <SourceAppendDialog
            v-model:visible="isAddSensorDialogVisible"
            from-state="source"
            :allowed-source-types="['source']"
            :building-id="buildingStore.building?._id"
            :address="buildingStore.building?.address ?? null"
            @cancel="onCancelAddSensor"
            @done="onDoneAddSensor"
        />
    </div>
</template>
